<template>
  <div class="fts-console-element">
    <draggable
      v-model="canvas[item]"
      v-bind="dragOptions"
      handle=".console-element-item__handle"
      draggable=".console-element-item-draggable"
      :disabled="!draggable"
      @start="onDrag = true"
      @end="onDrag = false"
      @change="$emit('list:change')"
      v-if="filterElement && filterElement.length"
    >
      <div
        v-for="elementItem in filterElement"
        :key="`ce-${elementItem.id}`"
        :id="`ce-${elementItem.id}`"
        class="console-element-item-draggable"
        :class="{'console-element-item-draggable--active': selectedId === elementItem.id}"
      >
        <div
          class="console-element-item"
          :class="{'console-element-item--disabled': isDisabled(elementItem)}"
          @click="!isDisabled(elementItem) && selectConsoleElement(elementItem)"
          v-if="elementItem"
        >
          <!-- handle -->
          <font-awesome-icon
            :icon="['fas', 'grip-vertical']"
            class="console-element-item__handle"
            v-if="draggable"
          />
          <!-- content -->
          <div class="console-element-item__content">
            <!-- wrapper -->
            <div class="console-element-item__content-wrapper">
              <!-- info -->
              <div
                class="console-element-info"
                :class="[
                  {'console-element-info--activity': item == 'activities'},
                  {'console-element-info--action': item === 'actions'},
                  {'console-element-info--document': item === 'documents'}
                ]"
                :style="{'border-color': elementItem.color}"
              >
                <!-- emoji -->
                <div class="console-element-emoji-wrapper" v-if="elementItem.emoji">
                  <fts-moji
                    :emoji="elementItem.emoji"
                    :size="14"
                    class="console-element-emoji"
                  />
                </div>
                <!-- information icon -->
                <fts-tippy
                  :content="elementItem.displayName"
                  :options="{ delay: tippyDelay }"
                  class="console-element-icon-wrapper"
                  :class="{'console-element-icon-wrapper--system': system}"
                  v-if="item === 'information'"
                >
                  <i
                    slot="trigger"
                    class="console-element-icon"
                    :class="informationIcon(elementItem.type)"
                  ></i>
                </fts-tippy>
                <!-- document icon -->
                <fts-tippy :options="{ delay: tippyDelay }" class="console-element-fa" v-if="item === 'documents'">
                  <template #trigger>
                    <font-awesome-icon
                      class="fts-file-pdf"
                      :icon="['far', 'file-pdf']"
                      v-if="elementItem.type === 'PDF'"
                    />
                    <font-awesome-icon
                      class="fts-file-excel"
                      :icon="['far', 'file-alt']"
                      v-else-if="elementItem.type === 'Spreadsheet'"
                    />
                    <font-awesome-icon
                      class="fts-file-html"
                      :icon="['far', 'file-code']"
                      v-else-if="elementItem.type === 'HTML'"
                    />
                    <font-awesome-icon
                      class="fts-file-default"
                      :icon="['far', 'file']"
                      v-else
                    />
                  </template>
                  <template #content>
                    <span v-if="elementItem.type === 'PDF'">PDF</span>
                    <span v-else-if="elementItem.type === 'File'">File</span>
                    <span v-else-if="elementItem.type === 'Spreadsheet'">
                      Spreadsheet
                    </span>
                    <span v-else>HTML</span>
                  </template>
                </fts-tippy>
                <!-- state -->
                <fts-state
                  :state="{
                    id: elementItem.id,
                    name: elementItem.name || '(undefined)',
                    color: elementItem.color,
                  }"
                  class="console-element-state"
                  v-if="item === 'states'"
                />
                <!-- flow -->
                <div class="console-element-flow" v-else-if="item === 'flows'">
                  <!-- from state -->
                  <fts-state
                    :state="{
                      name: getState(elementItem.fromStateId).name,
                      color: getState(elementItem.fromStateId).color,
                    }"
                    class="flow-state"
                    v-if="
                      elementItem.fromStateId && getState(elementItem.fromStateId)
                    "
                  />
                  <fts-state
                    :state="{ name: '(any state)' }"
                    class="flow-state"
                    v-else
                  />
                  <!-- direction -->
                  <font-awesome-icon
                    :icon="['fas', 'right-long']"
                    class="flow-direction"
                  />
                  <!-- activity -->
                  <div
                    class="flow-activity"
                    v-if="getActivity(elementItem.activityId)"
                  >
                    <fts-moji
                      :size="14"
                      :emoji="getActivity(elementItem.activityId).emoji"
                      class="flow-activity-emoji"
                      v-if="getActivity(elementItem.activityId).emoji"
                    />
                    <div
                      class="flow-activity-name"
                      :style="{
                        color: getActivity(elementItem.activityId).color,
                      }"
                    >
                      {{ getActivity(elementItem.activityId).name }}
                    </div>
                  </div>
                  <div class="flow-no-activity" v-else>(no activity)</div>
                  <!-- direction -->
                  <font-awesome-icon
                    :icon="['fas', 'right-long']"
                    class="flow-direction"
                  />
                  <!-- to state -->
                  <template
                    v-if="
                      elementItem.conditional &&
                      elementItem.conditionalStates &&
                      elementItem.conditionalStates.length
                    "
                  >
                    <template
                      v-for="(state, stateIndex) in elementItem.conditionalStates"
                    >
                      <div
                        :key="`${state.id}-or`"
                        class="flow-conditional"
                        v-if="stateIndex"
                      >
                        or
                      </div>
                      <fts-state
                        :key="state.id"
                        :state="{
                          name: getState(state.toStateId).name,
                          color: getState(state.toStateId).color,
                        }"
                        class="flow-state"
                        v-if="getState(state.toStateId)"
                      />
                    </template>
                  </template>
                  <fts-state
                    :state="{
                      name: getState(elementItem.toStateId).name,
                      color: getState(elementItem.toStateId).color,
                    }"
                    class="flow-state"
                    v-else-if="
                      elementItem.toStateId && getState(elementItem.toStateId)
                    "
                  />
                  <fts-state
                    :state="{ name: '(any state)' }"
                    class="flow-state"
                    v-else
                  />
                </div>
                <!-- name -->
                <div class="console-element-name" v-else>
                  {{elementName(elementItem)}}
                </div>
              </div>
              <!-- badge:disabled -->
              <fts-tippy
                content="Module doesn't support this capability"
                class="console-element-badge-tippy"
                v-if="isDisabled(elementItem)"
              >
                <div class="console-element-badge" slot="trigger" >
                  unsupported
                </div>
              </fts-tippy>
              <!-- badge:default -->
              <template v-else>
                <!-- module -->
                <fts-label-group
                  :item="getModule(elementItem.connectionId)"
                  emoji
                  :emoji-size="12"
                  class="console-element-badge"
                  v-if="elementItem.connectionId && getModule(elementItem.connectionId)"
                />
                <!-- formula (computed field) -->
                <fts-tippy
                  content="Computed (formula) field"
                  class="console-element-badge-tippy"
                  v-if="item === 'information' && isFormula(elementItem)"
                >
                  <div class="console-element-badge console-element-badge--primary" slot="trigger">
                    <i class="fts-icon-formula console-element-badge-icon"></i>formula
                  </div>
                </fts-tippy>
                <!-- system (information) -->
                <fts-tippy
                  trigger-class="console-element-badge--icon-wrapper"
                  :content="elementItem.description"
                  class="console-element-badge-tippy"
                  v-if="type === 'information' && system"
                >
                  <font-awesome-icon
                    slot="trigger"
                    :icon="['fas', 'info-circle']"
                    class="console-element-badge console-element-badge--icon"
                  />
                </fts-tippy>
                <!-- standard activity -->
                <fts-tippy
                  trigger-class="console-element-badge--icon-wrapper"
                  :content="getDescription(elementItem)"
                  class="console-element-badge-tippy"
                  v-else-if="type === 'standardActivity' && getDescription(elementItem)"
                >
                  <font-awesome-icon
                    slot="trigger"
                    :icon="['fas', 'info-circle']"
                    class="console-element-badge console-element-badge--icon"
                  />
                </fts-tippy>
                <!-- standard:initial -->
                <fts-btn
                  sm
                  color="secondary"
                  text-transform-init
                  box-shadow-none
                  class="console-element-btn"
                  data-v-step="studio-tour-start"
                  v-if="
                    type === 'standardActivity' &&
                    elementItem.id === 'createEditView' &&
                    (canvas.information && canvas.information.length === 0)
                  "
                >
                  Start here
                </fts-btn>
                <!-- classification -->
                <fts-tippy
                  :options="{
                    interactive: true,
                    placement: 'left-start',
                    delay: [500 , 0],
                    theme: 'light-border',
                  }"
                  popper-prevent-overflow
                  append-box-class="tippy-box-form-preview"
                  append-trigger-class="console-element-badge--icon-wrapper"
                  append-content-class="form-preview"
                  :key="isClassification(elementItem)"
                  class="console-element-badge-tippy"
                  v-else-if="isClassification(elementItem)"
                >
                  <i
                    class="
                      fts-icon-form
                      console-element-badge console-element-badge--icon
                      console-element-badge--primary
                    "
                    slot="trigger"
                  ></i>
                  <template #content>
                    <div class="form-preview__header">
                      Information for {{elementItem.name}}
                    </div>
                    <div class="form-preview__content fts-scrollbar">
                      <template v-if="classificationInformation(elementItem) && classificationInformation(elementItem).length">
                        <div
                          v-for="x in classificationInformation(elementItem)"
                          :key="x.id"
                          class="form-preview-information"
                        >
                          <i class="form-preview-information-icon" :class="informationIcon(x.type)"></i>
                          <div>
                            {{x.displayName}}
                          </div>
                        </div>
                      </template>
                      <div class="form-preview-blank" v-else>
                        User-defined form.
                      </div>
                    </div>
                  </template>
                </fts-tippy>
                <!-- classification:auto -->
                <fts-tippy
                  content="Auto-generated form based on information"
                  class="console-element-badge-tippy"
                  v-else-if="elementItem.id == 'createEditView'"
                >
                  <div class="console-element-badge" slot="trigger" >
                    auto
                  </div>
                </fts-tippy>
                <!-- classification:default -->
                <fts-tippy
                  content="Using default form"
                  class="console-element-badge-tippy"
                  v-else-if="
                    elementItem.id == 'create' ||
                    elementItem.id == 'edit' ||
                    elementItem.id == 'view' ||
                    elementItem.id == 'quickView'
                  "
                >
                  <div class="console-element-badge" slot="trigger" >
                    default
                  </div>
                </fts-tippy>
                <!-- internal -->
                <div class="console-element-badge" v-if="elementItem.internal">
                  automation only
                </div>
                <!-- skip history -->
                <div class="console-element-badge" v-if="elementItem.skipHistory">
                  skip history
                </div>
                <!-- initial -->
                <div class="console-element-badge" v-if="elementItem.initial">
                  initial
                </div>
                <!-- menu:default -->
                <fts-tippy
                  content="Default listing provided automatically. It will be replaced once you add your own listing."
                  class="console-element-badge-tippy"
                  v-if="type === 'menu' && elementItem.default"
                >
                  <div class="console-element-badge" slot="trigger">
                    default
                  </div>
                </fts-tippy>
                <!-- development -->
                <div class="console-element-badge" v-if="elementItem.development">
                  {{elementItem.development}}
                </div>
                <!-- data source -->
                <fts-tippy v-if="elementItem.dataSource">
                  <div class="console-element-badge fts-lowercase">
                    {{ elementItem.dataSource }}
                  </div>
                  <template #content>
                    <template v-if="elementItem.dataSourceId === 'logicService' && elementItem.logicServiceName">
                      Data source from <span class="fts-lowercase">{{elementItem.dataSource}}</span> ({{elementItem.logicServiceName}})
                    </template>
                    <template v-else>
                      Data source from <span class="fts-lowercase">{{elementItem.dataSource}}</span>
                    </template>
                  </template>
                </fts-tippy>
                <!-- list -->
                <div
                  class="console-element-list"
                  v-if="Boolean(elementItem.menus && elementItem.menus.length)"
                >
                  <div class="console-element-list-container">
                    <template v-for="(menu, ind) in elementItem.menus">
                      <div class="console-element-list-wrapper" :key="`menu-${menu}-${ind}`">
                        <fts-label-group
                          :item-id="menu"
                          :list="menus"
                          emoji
                          :emoji-size="12"
                        />
                        <span v-if="ind !== elementItem.menus.length - 1">,</span>
                      </div>
                    </template>
                  </div>
                </div>
              </template>
            </div>
            <!-- action -->
            <div class="console-element-item__content-action" v-if="!isDisabled(elementItem)">
              <template v-if="isAutomation(elementItem.id)">
                <!-- notification:action -->
                <fts-event-toggle
                  event-type="notification"
                  class="console-element-automation-action stopPropagation"
                  @open="addAutomation(elementItem, 'notification')"
                />
                <!-- automation:action -->
                <fts-event-toggle
                  class="console-element-automation-action stopPropagation"
                  @open="addAutomation(elementItem, 'automation')"
                />
              </template>
              <template v-if="authorization || (authorizationRule && authorizationRule(elementItem))">
                <!-- authorization:import -->
                <fts-auth
                  type="import"
                  :title="elementName(elementItem)"
                  class="console-element-authorization stopPropagation"
                  v-if="elementItem.id === 'import'"
                />
                <!-- authorization:default -->
                <fts-auth
                  :unitId="elementItem.id"
                  :type="authorizationType"
                  :title="elementName(elementItem)"
                  class="console-element-authorization stopPropagation"
                  v-else
                />
              </template>
              <!-- actions -->
              <fts-dropdown-select
                placement="left-start"
                strategy-fixed
                prevent-overflow-tether-off
                :prevent-overflow-boundary="parentId"
                :offset="[0, 0]"
                append-content-class="ce-content ce-dropdown-menu"
                :z-index="10"
                class="console-element-actions stopPropagation"
                @selected="executeAction(elementItem, $event)">
                <font-awesome-icon
                  slot="toggle"
                  class="console-element-action-trigger"
                  :icon="['fas', 'ellipsis-v']"
                  fixed-width
                />
                <template #menu="{select, defaultOptionClass}">
                  <template v-if="system">
                    <div
                      class="ce-action-option"
                      :class="defaultOptionClass"
                      @click="select('edit')">
                      <font-awesome-icon :icon="['far', 'file']" class="ce-action-option-icon" />View
                    </div>
                  </template>
                  <template v-else>
                    <div
                      class="ce-action-option"
                      :class="defaultOptionClass"
                      @click="select('edit')"
                    >
                      <font-awesome-icon :icon="['far', 'edit']" class="ce-action-option-icon" />Edit
                    </div>
                    <div
                      class="ce-action-option"
                      :class="defaultOptionClass"
                      @click="select('delete')"
                      v-if="editable"
                    >
                      <font-awesome-icon :icon="['far', 'trash-alt']" class="ce-action-option-icon" />Delete
                    </div>
                    <div
                      class="ce-action-option"
                      :class="defaultOptionClass"
                      @click="select('duplicate')"
                      v-if="creatable"
                    >
                      <font-awesome-icon :icon="['far', 'clone']" class="ce-action-option-icon" />Duplicate
                    </div>
                    <div
                      class="ce-action-option"
                      :class="defaultOptionClass"
                      @click="select('api')"
                      v-if="isAPIDocumentation(elementItem)"
                    >
                      <font-awesome-icon :icon="['fas', 'code']" class="ce-action-option-icon" />API 
                      <fts-release-badge alpha class="fts-align-top" v-if="type === 'logicService'" />
                    </div>
                  </template>
                </template>
              </fts-dropdown-select>
            </div>
          </div>
          <!-- ai annotations (actor / instruction / hint / confidence) -->
          <fts-ai-annotation
            :actor="elementItem.actor"
            :instruction="elementItem.ai_instruction"
            :hint="elementItem.ai_hint"
            :confidence="elementItem.confidence_threshold"
            :editable="editable && !system && annotationFields.length > 0"
            :fields="annotationFields"
            :boundary="parentId"
            @save="saveAnnotation(elementItem, $event)"
          />
          <!-- automation -->
          <div class="console-element-item__automation" v-if="!isDisabled(elementItem)">
            <div
              class="console-element-item__automation-container"
              v-if="
                getAutomation(elementItem.id) &&
                getAutomation(elementItem.id).length
              "
            >
              <!-- <fts-event
                :event="{ id: 'Subscription', count: 4, name: 'Subscription' }"
                :state-indicator="false"
                class="stopPropagation"
                @open="openSubscription(elementItem)"
                v-if="isDevelopment"
              /> -->
              <fts-event
                v-for="event in getAutomation(elementItem.id)"
                :key="event.id"
                :event="event"
                class="stopPropagation"
                @open="openAutomation"
              />
            </div>
          </div>
        </div>
        <!-- information fields -->
        <template
          v-if="
            item === 'information' &&
            elementItem &&
            elementItem.fields &&
            elementItem.fields.length &&
            !onDrag
          "
        >
          <div
            class="console-element-item console-element-item-child"
            v-for="column in elementItem.fields"
            :key="`${column.id}`"
            @click="selectConsoleElement(column)"
          >
            <!-- content -->
            <div class="console-element-item__content">
              <!-- wrapper -->
              <div class="console-element-item__content-wrapper">
                <!-- info -->
                <div class="console-element-info">
                  <!-- emoji -->
                  <fts-moji
                    :emoji="elementItem.emoji"
                    :size="16"
                    class="console-element-emoji"
                    v-if="elementItem.emoji"
                  />
                  <!-- information icon -->
                  <fts-tippy
                    :content="column.displayName"
                    :options="{ delay: tippyDelay }"
                    class="console-element-icon-wrapper"
                    v-if="item === 'information'"
                  >
                    <i
                      slot="trigger"
                      class="console-element-icon"
                      :class="informationIcon(column.type)"
                    ></i>
                  </fts-tippy>
                  <!-- name -->
                  <div class="console-element-name">
                    {{ column.displayName }}
                  </div>
                </div>
                <!-- module -->
                <fts-label-group
                  :item="getModule(column.connectionId)"
                  emoji
                  :emoji-size="12"
                  class="console-element-badge"
                  v-if="column.connectionId && getModule(column.connectionId)"
                />
                <!-- formula (computed column) -->
                <fts-tippy
                  content="Computed (formula) field"
                  class="console-element-badge-tippy"
                  v-if="isFormula(column)"
                >
                  <div class="console-element-badge console-element-badge--primary" slot="trigger">
                    <i class="fts-icon-formula console-element-badge-icon"></i>formula
                  </div>
                </fts-tippy>
              </div>
              <!-- action -->
              <div class="console-element-item__content-action">
                <!-- per-column access lock (information-level authorization on the sub-field
                     id) - same predicate as the fields editor in FTSInformationDesignModal -->
                <fts-auth
                  :unitId="column.id"
                  type="information"
                  :title="column.displayName"
                  class="console-element-authorization stopPropagation"
                  v-if="(authorization || authorizationRule) && column.id && !column.system"
                />
                <!-- actions -->
                <fts-dropdown-select
                  placement="left-start"
                  strategy-fixed
                  prevent-overflow-tether-off
                  :prevent-overflow-boundary="parentId"
                  :offset="[0, 6]"
                  append-content-class="ce-content ce-dropdown-menu"
                  :z-index="10"
                  class="console-element-actions stopPropagation"
                  @selected="executeAction(column, $event)">
                  <font-awesome-icon
                    slot="toggle"
                    class="console-element-action-trigger"
                    :icon="['fas', 'ellipsis-v']"
                    fixed-width
                  />
                  <template #menu="{select, defaultOptionClass}">
                    <div
                      class="ce-action-option"
                      :class="defaultOptionClass"
                      @click="select('edit')">
                      <font-awesome-icon :icon="['far', 'edit']" class="ce-action-option-icon" />Edit
                    </div>
                    <div
                      class="ce-action-option"
                      :class="defaultOptionClass"
                      @click="select('delete')"
                      v-if="editable">
                      <font-awesome-icon :icon="['far', 'trash-alt']" class="ce-action-option-icon" />Delete
                    </div>
                  </template>
                </fts-dropdown-select>
              </div>
            </div>
          </div>
        </template>
      </div>
    </draggable>
    <!-- action:load more -->
    <div class="console-element-footer" v-if="pageSize != null && pageSize != undefined">
      <fts-btn
        text
        text-transform-init
        full-width
        class="fts-font-normal"
        @click="loadMore = !loadMore" 
      >
        <template v-if="type === 'information' && system">
          {{loadMore ? "hide default information" : "show default information"}}
        </template>
        <template v-else>
          {{loadMore ? "show less" : "show more"}}
        </template>
      </fts-btn>
    </div>
    <!-- api schema modal -->
    <fts-api-schema-modal ref="ftsAPISchemaModal" />
    <!-- information-delete-confirmation -->
    <fts-information-delete-confirmation ref="ftsInformationDeleteConfirmation" />
    <!-- canvas guard (D11) -->
    <fts-canvas-guard-dialog ref="ftsCanvasGuardDialog" />
  </div>
</template>

<script>
import { authorizationStore } from '../store/authorizationStore';
import { store as canvasDesignStore } from "../store/canvasDesignStore";
import commons from "../commons/commons";

import draggable from "vuedraggable";

import FTSDropdownSelect from "../elements/FTSDropdownSelect";
import FTSMoji from "../elements/FTSMoji";
import FTSState from "../elements/FTSState";
import FTSTippy from "../elements/FTSTippy";
import FTSAuth from "../design/FTSAuth";
import FTSLabelGroup from "../elements/FTSLabelGroup";
import FTSBtn from "../elements/FTSBtn";
import FTSEvent from "./event/FTSEvent";
import FTSEventToggle from "./event/FTSEventToggle";
import FTSAPISchemaModal from './FTSAPISchemaModal';
import FTSInformationDeleteConfirmation from './FTSInformationDeleteConfirmation';
import FTSCanvasGuardDialog from './FTSCanvasGuardDialog';
import FTSAiAnnotation from './FTSAiAnnotation';
import canvasDesign from '../commons/canvasDesign';
import formDesign from '../commons/formDesign';

export default {
  props: {
    parentId: String,
    selectedId: [Number, String],
    canvas: Object,
    item: String,
    type: String,
    label: String,
    pageSize: Number,
    draggable: {
      type: Boolean,
      default: true,
    },
    creatable: {
      type: Boolean,
      default: true,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    authorization: {
      type: Boolean,
      default: true,
    },
    authorizationRule: Function,
    automation: {
      type: Boolean,
      default: true,
    },
    system: Boolean,
    disableList: Array,
  },
  components: {
    draggable,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-moji": FTSMoji,
    "fts-state": FTSState,
    "fts-tippy": FTSTippy,
    "fts-auth": FTSAuth,
    "fts-label-group": FTSLabelGroup,
    "fts-btn": FTSBtn,
    "fts-event": FTSEvent,
    "fts-event-toggle": FTSEventToggle,
    "fts-api-schema-modal": FTSAPISchemaModal,
    "fts-information-delete-confirmation": FTSInformationDeleteConfirmation,
    "fts-canvas-guard-dialog": FTSCanvasGuardDialog,
    "fts-ai-annotation": FTSAiAnnotation
  },
  data: () => ({
    dragOptions: commons.dragOptions,
    onDrag: false,
    tippyDelay: [1000, 0],
    loadMore: false,
  }),
  methods: {
    getDescription(item) {
      switch(item.id) {
        case 'createEditView':
          return 'Default form if create, edit, quick view or view does not have form. If form is not set it will automatically generated based on Information';
        case 'create':
          return 'To create or add new entry';
        case 'edit':
          return 'To update or edit an entry';
        case 'quickView':
          return 'To quick view an entry';
        case 'view':
          return 'To view an entry';
        case 'delete':
          return 'To delete an entry';
        case 'changeStatus':
          return 'To change state of an entry';
        case 'duplicate':
          return 'To make a copy of an entry';
        case 'history':
           return 'To view history of an entry';
        case 'comment':
          return 'To comment on an entry';
        case 'import':
          return 'To import or merge an entry using spreadsheet';
      }
    },
    selectConsoleElement(data) {
      if (commons.elementStopPropagation()) return;
      this.update(data);
    },
    /* persist inline edits to an element's AI annotation; only the keys present in
       the payload are applied, so fields the element doesn't expose stay untouched */
    saveAnnotation(element, payload) {
      if ("hint" in payload) this.$set(element, "ai_hint", payload.hint);
      if ("actor" in payload) this.$set(element, "actor", payload.actor);
      if ("instruction" in payload) this.$set(element, "ai_instruction", payload.instruction);
      if ("confidence" in payload) this.$set(element, "confidence_threshold", payload.confidence);
      window.eventBus.$emit("builderSave", { type: this.type, action: "update", data: element });
    },
    update(data) {
      this.$emit("update", data);
    },
    /* automations */
    getAutomation(id) {
      return _.sortBy(this.triggers.filter(x => x.item == id), ['actionType']);
    },
    openAutomation(item) {
      if (!item.type)
        item.type = this.type;

      this.$emit("openAutomation", item);
    },
    addAutomation(element, actionType) {
      this.openAutomation({
        item: element.id,
        type: this.type,
        trigger: "execute",
        actionType: actionType,
      });
    },
    duplicate(data) {
      let cloned = _.cloneDeep(data);
      if (this.type === "information") {
        function generateNewIdentity(field) {
          field.id = commons.generateFieldName();
          field.fieldName = commons.generateFieldName();
        }

        generateNewIdentity(cloned);
        cloned.displayName = `${cloned.displayName} (Copy)`;
  
        if ((cloned.type === 14 || cloned.type === 15) && Array.isArray(cloned.fields)) {
          cloned.fields.forEach(x => {
            generateNewIdentity(x);
            if (x.type === 14 && Array.isArray(x.fields)) {
              x.fields.forEach(y => {
                generateNewIdentity(y);
              });
            }
          });
        }
      } else {
        cloned.id = this.type === "menu" ? commons.nanoID() : commons.newUUID();
        if (this.type == "flow") {
          if (cloned.conditional && Array.isArray(cloned.conditionalStates)) {
            cloned.conditionalStates.forEach((x) => {
              x.id = commons.newUUID();
            });
          } 
        } else {
          cloned.name = `${cloned.name} (Copy)`;
        }
      }
      /* duplicate triggers */
      try {
        let filterTriggers = _.cloneDeep(this.canvas.triggers.filter((x) => x.item === data.id));

        if (filterTriggers.length) {
          if (this.type === "activity") {
            let filterAutomations = filterTriggers.filter(x => x.actionType === "automation");
            filterTriggers = filterTriggers.filter(x => x.actionType !== "automation");

            const updateActions = (blocks) => {
              Object.values(blocks).forEach(block => {
                if (block.name === "sequence" && block.data && Array.isArray(block.data.actions)) {
                  block.data.actions.forEach(action => {
                    if (action.activityId === data.id) {
                      action.activityId = cloned.id;
                    }
                  });
                }
              });
            }

            if (filterAutomations.length) {
              filterAutomations.forEach(x => {
                if (x.automationBefore && x.automationBefore.blocks) {
                  updateActions(x.automationBefore.blocks);
                }

                if (x.automation && x.automation.blocks) {
                  updateActions(x.automation.blocks);
                }
              });

              filterTriggers = filterTriggers.concat(filterAutomations);
            }
          }

          filterTriggers = filterTriggers.map(x => (
            canvasDesign.initTrigger({
              ...x, 
              id: null, 
              item: cloned.id
            })
          ));

          this.$emit("update:canvas", {
            ...this.canvas,
            triggers: [
              ...this.canvas.triggers,
              ...filterTriggers
            ]
          });
        }
      } catch(error) {
        console.error("Failed to clone triggers.", error);
        this.$toasted.global.error({ message: "Unable to clone notification or automation." });
      }

      this.canvas[this.item].push(cloned);
      window.eventBus.$emit("builderSave", {'type': this.type, 'action': 'create', 'data': cloned});
    },
    async deleteItem(data) {
      /* D11/D14: the canvas guard replaces the per-type confirmations for the console's
         non-leaf units while the alpha gate is on. It sweeps the FULL referrer set
         (designs, listings, filters, formulas, automations, permissions), offers the
         cascade as a checklist, and applies through the ops engine — which records exact
         inverses, so guarded deletes land in the apply history and become revertible.
         "Delete anyway" falls through to the legacy path below (designer authority). */
      if (this.guardEnabled && this.guardAddress(data)) {
        const handled = await this.guardedDelete(data);
        if (handled) return;
      }
      if (this.type === 'information') {
        let canvas = _.cloneDeep(this.canvas);
        const res = await this.$refs.ftsInformationDeleteConfirmation.show({
          canvas: canvas,
          information: data
        });
        if (res === true) {
          canvasDesign.deleteInformation(this.canvas, data.id);
          window.eventBus.$emit("builderSave", {'type': this.type, 'action': 'delete', 'data': data});
        }
      } else {
        const res = await this.$confirmation.show({
          type: 'delete',
          title: this.label,
          text: data.name,
          textEmoji: ['activity', 'menu', 'action'].indexOf(this.type) > -1 ? data.emoji : null,
          textColor: ['activity', 'action'].indexOf(this.type) > -1 ? data.color : null,
          backgroundColor: this.type === 'state' ? data.color : null
        });

        if (res === true) {
          if (this.type === "activity") {
            canvasDesign.deleteActivity(this.canvas, data);
          } else if (this.type === "menu") {
            canvasDesign.deleteMenu(this.canvas, data);
          } else if (this.type === "state") {
            canvasDesign.deleteState(this.canvas, data);
          } else if (this.type === "document") {
            canvasDesign.deleteDocument(this.canvas, data);
          } else if (this.type === "action") {
            canvasDesign.deleteAction(this.canvas, data);
          } else {
            this.canvas[this.item].splice(
              this.canvas[this.item].indexOf(data),
              1
            );
          }

          window.eventBus.$emit("builderSave", {'type': this.type, 'action': 'delete', 'data': data});
        }
      }
    },
    /* canvas-ops address for the console unit types the guard covers; null = not guarded
       (leaf/unknown types keep their existing flow) */
    guardAddress(data) {
      switch (this.type) {
        case 'information': return [["information", data.id]];
        case 'state': return [["states", data.id]];
        case 'activity': return [["activities", data.id]];
        case 'menu': return [["menus", data.id]];
        case 'document': return [["documents", data.id]];
        case 'action': return [["actions", data.id]];
        default: return null;
      }
    },
    /* Returns true when the guard handled the interaction (applied or cancelled);
       false = fall through to the legacy delete path ("Delete anyway"). */
    async guardedDelete(data) {
      let composed;
      try {
        composed = await canvasDesignStore.dispatch('guardedRemoveCompose', {
          address: this.guardAddress(data),
        });
      } catch (error) {
        console.error("canvas guard compose failed — falling back to the legacy delete", error);
        return false;
      }
      if (!composed || composed.errors) return false;

      const res = await this.$refs.ftsCanvasGuardDialog.show({
        mode: 'delete',
        unitLabel: this.label,
        subject: this.elementName(data),
        composed,
      });
      if (res === false) return true; // cancelled
      if (res.mode === 'legacy') return false; // designer forces the old path

      const result = await canvasDesignStore.dispatch('applyCanvasOps', {
        ops: res.ops,
        authRows: res.authRows,
        source: 'guard',
        label: `Delete ${this.label ? this.label.toLowerCase() : this.type} '${this.elementName(data)}'`,
      });
      if (!result.ok) {
        const first = result.errors.concat(...result.ops.map(o => o.errors))[0];
        this.$toasted.global.error({
          message: first ? first.message : "The delete could not be applied cleanly.",
        });
        return true;
      }
      window.eventBus.$emit("builderSave", {'type': this.type, 'action': 'delete', 'data': data});
      return true;
    },
    openSubscription(item) {
      this.$emit("openSubscription", item);
    },
    elementName(item) {
      if (this.type === 'information')
        return item.displayName;
      else if (this.type === 'standardActivity' && item.id === 'createEditView')
        return 'Default (Create + Edit + View)';
      else
        return item.name;
    },
    informationIcon(type) {
      return commons.informationIcon(type);
    },
    /* a computed (formula) field/column: `computed` is the canonical flag, `formulaMode` covers
       fields authored before the flag existed (the modal backfills `computed` from it). */
    isFormula(item) {
      return !!(item && (item.computed || item.formulaMode));
    },
    getModule(id) {
      if (id && this.$store.getters.workspaceModules) {
        const findModule = this.$store.getters.workspaceModules.find((x) => x.id == id);
        if (findModule) 
          return {
            id: findModule.id,
            name: findModule.name,
            emoji: findModule.emoji,
          }
      }
    },
    getState(id) {
      if (this.canvas.states)
        return this.canvas.states.find((x) => x.id === id);
    },
    getActivity(id) {
      if (this.canvas.activities)
        return this.canvas.activities.find((x) => x.id === id);

      return {};
    },
    selectModule(moduleId) {
      this.$emit("moduleSelected", {
        id: moduleId,
      });
    },
    executeAction(data, type) {
      if (type === 'duplicate')
        this.duplicate(data);
      else if (type === 'delete')
        this.deleteItem(data);
      else if (type === 'api')
        this.openFormPayloadModal(data);
      else if (type === 'edit')
        this.update(data);
      else
        console.warn(`undefined "type" ${type}`);
    },
    classificationInformation(item) {
      if (this.type === 'document' && item.hasUserInput && item.userInput && item.userInput.rows)
        return commons.classificationInformation(item.userInput.rows, this.information); 
      else if (item.design && item.design.rows)
        return commons.classificationInformation(item.design.rows, this.information);

      return [];
    },
    isClassification(item) {
      return Boolean(
        ((this.type === 'activity' || this.type == 'standardActivity') && item.design) ||
        (this.type === 'document' && item.hasUserInput)
      );
    },
    isAutomation(itemId) {
      if (this.type === "standardActivity" && !["createEditView", "create", "edit"].includes(itemId))
        return false;

      return this.automation;
    },
    isAPIDocumentation(item) {
      return (
        (
          this.type === 'standardActivity' &&
          ['createEditView', 'create', 'edit', 'view', 'quickView', 'delete', 'changeStatus', 'duplicate'].includes(item.id)
        ) || 
        this.type === 'activity' ||
        this.type === 'menu' ||
        (this.type === 'logicService' && this.isInternal)
      );
    },
    async openFormPayloadModal(item) {
      const getPayload = () => {
        if (this.type === 'menu') {
          return {
            'type': 'listing',
            'listingId': item.id,
            'information': item.list && item.list.columns.filter((x) => !x.system)
          }
        } else if (this.type === 'activity' || this.type === 'standardActivity') {
          return {
            'type': 'form',
            'activityId': item.id,
            'information': formDesign.getActivityFormInformation(this.canvas, item.id, this.type),
          }
        } else if (this.type === 'logicService') {
          return {
            'type': 'logicService'
          }
        }
      }
      this.$refs.ftsAPISchemaModal.show({
        'workspaceId': canvasDesignStore.getters.workspaceId,
        'moduleId': canvasDesignStore.state.vectorId,
        'item': {
          'name': item.name,
          'emoji': item.emoji,
          'color': item.color
        },
        ...getPayload()
      }, this.canvas);
    },
    isDisabled(item) {
      return this.disableList && this.disableList.some(x => x.id === item.id && x.type === this.type);
    },
  },
  computed: {
    /* D14 alpha gate for the D11 canvas guard — and only when this instance edits the
       live store draft (the system-default instance binds a synthetic canvas). */
    guardEnabled() {
      return canvasDesignStore.getters.canvasOpsEnabled &&
        !this.system &&
        this.canvas === canvasDesignStore.state.canvas;
    },
    /* which AI annotation fields are editable for this element type */
    annotationFields() {
      switch (this.type) {
        case "information":
          return ["hint"];
        case "state":
          return ["hint", "instruction"];
        case "activity":
        case "standardActivity":
          return ["hint", "actor", "instruction", "confidence"];
        default:
          return [];
      }
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    beta() {
      return authorizationStore.getters.isBeta || this.$store.getters.workspaceBeta;
    },
    isDevelopment() {
      return authorizationStore.getters.isDevelopment;
    },
    authorizationType() {
      switch (this.item) {
        case "information":
          return "information";
        case "activities":
        case "standardActivities":
          return "activity";
        case "states":
          return "state";
        // case 'flows':
        //   return 'flow'
        case "menus":
          return "menu";
        // case 'documents':
        //   return 'document'
        // case 'actions':
        //   return 'action'
        // case 'logicServices':
        //   return 'logicService'
      }
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    menus() {
      return this.$store.state.canvas.menus || [];
    },
    triggers() {
      return this.$store.getters.vectorTriggers;
    },
    searchKeyword() {
      return this.$store.state.filterKeyword;
    },
    searchKeywordToLower() {
      return this.$store.state.filterKeyword && this.$store.state.filterKeyword.toLowerCase();
    },
    filterElement() {
      let results = commons.isArray(this.canvas[this.item]) ? [...this.canvas[this.item]] : [];

      if (!this.beta && this.type === "standardActivity") {
        results = results.filter(x => x.id !== 'print');
      }

      // The legacy "defaultListing" placeholder row is a design carrier, not a view (R13): it stays
      // in the canvas until the Phase 2 app is the floor (R9) but is never listed. Its design is
      // edited as the pinned "Default" page design above the views.
      if (this.type === "menu") {
        results = results.filter(x => !(x && x.id === 'defaultListing' && x.default === true));
      }

      if (!!this.searchKeywordToLower) {
        if (!this.label.toLowerCase().indexOf(this.searchKeywordToLower) > -1) {
          results = results.filter(x => {
            if (
              (x.displayName && x.displayName.toLowerCase().indexOf(this.searchKeywordToLower) > -1) ||
              (x.name && x.name.toLowerCase().indexOf(this.searchKeywordToLower) > -1)
            ) {
              return true;
            }

            if (
              this.type === "information" && 
              Array.isArray(x.fields) && 
              x.fields.some(y => 
                y.displayName && 
                y.displayName.toLowerCase().indexOf(this.searchKeywordToLower) > -1
              )
            ) {
              return true;
            }

            if (this.type === "flow") {
              if (x.activityId) {
                const flowActivity = canvasDesignStore.getters.canvasActivities.find(y => y.id === x.activityId);

                if (
                  flowActivity && 
                  flowActivity.name && 
                  flowActivity.name.toLowerCase().indexOf(this.searchKeywordToLower) > -1
                ) {
                  return true;
                }
              }

              const isFlowState = (list) =>
                list.some(y => {
                  const flowState = canvasDesignStore.getters.canvasStates.find(z => z.id === y);
                  return flowState && flowState.name && flowState.name.toLowerCase().indexOf(this.searchKeywordToLower) > -1;
                });

              if (x.fromStateId && isFlowState([x.fromStateId])) {
                return true;
              }
              
              if (x.conditional) {
                if (
                  Array.isArray(x.conditionalStates) && 
                  isFlowState(x.conditionalStates.map(y => y.toStateId))
                ) {
                  return true;
                }
              } else if (x.toStateId && isFlowState([x.toStateId])) {
                return true;
              }
            }

            if (this.isInternal && x.id === this.searchKeyword) {
              return true;
            }

            if (this.type !== "flow" && Array.isArray(this.triggers)) {
              const hasTriggerMatch = this.triggers.some(y => {
                if (y.item === x.id) {
                  const isDescriptionMatch = y.description ? y.description.toLowerCase().includes(this.searchKeywordToLower) : false;
                  let isActivityIdMatch = false;

                  if (this.isInternal) {
                    try {
                      isActivityIdMatch = JSON.stringify(y).includes(`"activityId":"${this.searchKeyword}"`);
                    } catch(err) {
                      console.error('Error stringifying object:', err);
                    }
                  }

                  return isDescriptionMatch || isActivityIdMatch;
                }

                return false;
              });

              if (hasTriggerMatch) {
                return true;
              }
            }

            return false;
          });
        }
      }

      if (!this.loadMore && this.pageSize !== null && this.pageSize !== undefined) {
        results = results.slice(0, this.pageSize)
      }

      return results;
    },
  },
  watch: {
    selectedId: {
      handler(val) {
        val && this.$nextTick(() => {
          const el = document.getElementById(`ce-${val}`);
          this.$emit("scroll:to", el);
        });
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-console-element {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  .console-element-item-draggable {
    &.sortable-chosen {
      .console-element-item-child {
        display: none;
      }
    }
    &.console-element-item-draggable--active {
      box-shadow: 0px 0px 2px 2px rgba(var(--fts-primary-rgb), 0.125) inset;
      -webkit-box-shadow: 0px 0px 2px 2px rgba(var(--fts-primary-rgb), 0.125) inset;
      -moz-box-shadow: 0px 0px 2px 2px rgba(var(--fts-primary-rgb), 0.125) inset;
      background-color: rgba(var(--fts-primary-rgb), 0.05);
    }
  }
  .console-element-item {
    position: relative;
    padding: 0.5rem 0.375rem 0.5rem 1.375rem;
    cursor: pointer;
    &.console-element-item-child {
      padding-left: 2.5rem;
    }
    &:hover {
      background-color: var(--fts-gray-100);
      .console-element-item__handle,
      .console-element-authorization .auth-toggle:not(.lock),
      .console-element-action-trigger,
      .console-element-automation-action {
        visibility: visible !important;
      }
    }
    &.console-element-item--disabled {
      .console-element-info {
        opacity: 0.5;
      }
      cursor: default;
    }
    .console-element-item__handle {
      @include absolute-center-y;
      left: 0.375rem;
      color: var(--fts-default);
      font-size: 0.75rem;
      visibility: hidden;
      cursor: grab;
    }
    .console-element-item__content {
      display: flex;
      .console-element-item__content-wrapper {
        flex: 1;
        min-width: 0;
        @include flex-gap($display: flex, $gap: .375rem);
        align-items: center;
      }
      .console-element-item__content-action {
        display: inline-flex;
        align-content: center;
        align-self: flex-start;
        margin-left: .5rem;
        & > *:not(:first-child) {
          margin-left: .375rem;
        }
      }
      .console-element-info {
        min-width: 0;
        display: flex;
        &.console-element-info--activity {
          border: 1px solid var(--fts-primary);
        }
        &.console-element-info--action {
          border: 1px solid var(--fts-ternary-blue);
        }
        &.console-element-info--document {
          border: 1px solid var(--fts-ternary-green);
        }
        &.console-element-info--activity,
        &.console-element-info--action,
        &.console-element-info--document {
          align-items: center;
          justify-content: center;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          background-color: var(--fts-card-background);
        }
      }
      .console-element-flow {
        min-width: 0;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        margin: -0.175rem;
        cursor: pointer;
        .flow-state,
        .flow-activity,
        .flow-no-activity,
        .flow-direction,
        .flow-conditional {
          margin: 0.175rem;
        }
        .flow-activity,
        .flow-no-activity,
        .flow-conditional {
          font-size: 0.875rem;
          line-height: 1.5;
        }
        .flow-activity {
          min-width: 0;
          display: flex;
          align-items: center;
          .flow-activity-emoji {
            margin-right: 0.375rem;
          }
          .flow-activity-name {
            @include text-overflow-hidden;
            font-size: inherit;
            color: var(--fts-text);
          }
        }
        .flow-no-activity,
        .flow-direction,
        .flow-conditional {
          color: var(--fts-default);
        }
      }
      .console-element-name {
        font-size: 0.875rem;
        line-height: 1.5;
        color: var(--fts-text);
        word-break: break-word;
      }
      .console-element-btn {
        min-height: auto;
        height: auto;
        padding: 0.25rem 0.5rem;
      }
      .console-element-badge {
        @include user-select-none;
        align-self: center;
        cursor: default;
        &.console-element-badge--icon {
          font-size: 1rem;
          line-height: 1rem;
          color: var(--fts-default);
        }
        &:not(.console-element-badge--icon) {
          display: inline-flex;
          align-items: center;
          padding: 0.125rem 0.5rem;
          border: 1px solid var(--fts-border);
          border-radius: 0.25rem;
          background-color: var(--fts-gray-50);
          color: var(--fts-text);
          font-size: 0.75rem;
          line-height: 1rem;
        }
        &.console-element-badge--primary {
          &:not(.console-element-badge--icon) {
            @include theme() {
              background-color: rgba(theme-get('fts-primary'), .095);
            }
          }
          color: var(--fts-primary);
        }
        .console-element-badge-icon {
          margin-right: 0.25rem;
          font-size: 0.875rem;
          line-height: 1rem;
        }
      }
      .console-element-badge-tippy {
        align-self: center;
        .console-element-badge--icon-wrapper {
          display: flex;
          align-items: center;
        }
      }
      .console-element-list {
        min-width: 0;
        align-self: center;
        padding: 0.125rem 0.375rem;
        border-radius: 0.25rem;
        border: 1px solid var(--fts-gray-200);
        background-color: var(--fts-gray-50);
        .console-element-list-container {
          @include flex-gap($display: flex, $gapTop: 0.075rem, $gapLeft: 0.5rem);
          .console-element-list-wrapper {
            min-width: 0;
            display: flex;
            align-items: center;
            color: var(--fts-gray-600);
            font-size: 0.75rem;
            line-height: 1rem;
          }
        }
      }
      .console-element-authorization {
        display: flex;
        align-items: center;
        .auth-toggle {
          font-size: .875rem;
          &:not(.lock) {
            visibility: hidden;
          }
        }
      }
      .console-element-action-trigger {
        color: var(--fts-default);
        font-size: 0.875rem;
        cursor: pointer;
        visibility: hidden;
      }
      .console-element-automation-action {
        visibility: hidden;
      }
    }
    .console-element-item__automation {
      display: flex;
      align-items: center;
      .console-element-item__automation-container {
        @include flex-gap($display: flex, $gap: .375rem);
        padding-top: .375rem;
      }
    }
  }
  .console-element-footer {
    margin: 0.5rem 1rem;
  }
  .console-element-icon-wrapper {
    margin-right: 0.375rem;
    font-size: 0.875rem;
    .console-element-icon {
      line-height: 1.25rem;
    }
  }
  .console-element-emoji-wrapper {
    height: 1.25rem;
    display: flex;
    align-items: center;
    margin-right: 0.375rem;
  }
  .console-element-fa {
    margin-right: 0.375rem;
    font-size: 0.875rem;
    line-height: 1.5;
  }
}
/* dropdown menu */
.ce-content.ce-dropdown-menu {
  min-width: 120px;
  .ce-action-option {
    .ce-action-option-icon {
      margin-right: 0.375rem;
      color: var(--fts-default);
    }
  }
}
/* form preview */
.tippy-box-form-preview {
  padding: 0;
}
.form-preview {
  max-height: 300px;
  .form-preview__header {
    @include text-overflow-ellipsis;
    flex-shrink: 0;
    padding: 0.75rem;
    border-bottom: 1px solid var(--fts-border);
    font-size: 0.875rem;
    font-weight: 500;
  }
  .form-preview__content {
    padding-top: 0.375rem;
    padding-bottom: 0.375rem;
    .form-preview-information {
      display: flex;
      align-items: flex-start;
      padding: 0.375rem 0.75rem;
      font-size: 0.875rem;
      line-height: 1.25rem;
      .form-preview-information-icon {
        margin-right: 0.5rem;
        color: var(--fts-secondary);
        line-height: inherit;
      }
    }
    .form-preview-blank {
      padding: 0.375rem 0.75rem;
      font-size: 0.875rem;
      line-height: 1.25rem;
    }
  }
}
</style>